import { apiRequest } from '../../../api/client';
import type {
  CreateInvestigationResponseDto,
  InvestigationClosureInputDto,
  InvestigationContentTypeDto,
  MachineKnowledgeCandidatesResponseDto,
  InvestigationDetailResponseDto,
  InvestigationStatusDto,
  ListInvestigationsResponseDto,
} from '../types/investigation';
import type {
  InquiryResponseActionDto,
  InquiryResponseResultDto,
  InvestigationInquiriesResponseDto,
  InvestigationReasoningFreshnessResponseDto,
  InvestigationReasoningHistoryResponseDto,
  InvestigationReasoningResponseDto,
  ReasoningRefreshResultDto,
} from '../types/active-reasoning';

export function listInvestigations(machineId: string) {
  return apiRequest<ListInvestigationsResponseDto>(`/v1/machines/${encodeURIComponent(machineId)}/investigations`);
}

export function getInvestigation(investigationId: string) {
  return apiRequest<InvestigationDetailResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}`);
}

export function createInvestigation(machineId: string, title: string, objective: string) {
  return apiRequest<CreateInvestigationResponseDto>(`/v1/machines/${encodeURIComponent(machineId)}/investigations`, {
    method: 'POST',
    body: JSON.stringify({ title, objective }),
  });
}

export function promoteConversation(conversationId: string, title: string, objective: string, selectedTurnIds?: string[]) {
  return apiRequest<InvestigationDetailResponseDto>(`/v1/conversations/${encodeURIComponent(conversationId)}/promote`, {
    method: 'POST',
    body: JSON.stringify({ title, objective, ...(selectedTurnIds?.length ? { selected_turn_ids: selectedTurnIds } : {}) }),
  });
}

export function updateInvestigationStatus(investigationId: string, status: InvestigationStatusDto, closure?: InvestigationClosureInputDto) {
  return apiRequest<InvestigationDetailResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, ...(closure ? { closure } : {}) }),
  });
}

export function reviewInvestigationMachineKnowledge(investigationId: string, closure: InvestigationClosureInputDto) {
  return apiRequest<MachineKnowledgeCandidatesResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/knowledge-candidates`, {
    method: 'POST',
    body: JSON.stringify({ closure }),
  });
}

export function addInvestigationContent(
  investigationId: string,
  contentType: InvestigationContentTypeDto,
  text: string,
  supportingEvidenceIds: string[] = [],
) {
  return apiRequest<InvestigationDetailResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/content`, {
    method: 'POST',
    body: JSON.stringify({ content_type: contentType, text, supporting_evidence_ids: supportingEvidenceIds }),
  });
}

export function postInvestigationUpdate(investigationId: string, text: string) {
  return apiRequest<InvestigationDetailResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/updates`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}


export function getInvestigationReasoning(investigationId: string) {
  return apiRequest<InvestigationReasoningResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning`);
}

export function getInvestigationReasoningFreshness(investigationId: string) {
  return apiRequest<InvestigationReasoningFreshnessResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/freshness`);
}

export function refreshInvestigationReasoning(investigationId: string) {
  return apiRequest<ReasoningRefreshResultDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/refresh`, {
    method: 'POST',
  });
}

export function listInvestigationInquiries(investigationId: string) {
  return apiRequest<InvestigationInquiriesResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/inquiries`);
}

export function listInvestigationReasoningHistory(investigationId: string) {
  return apiRequest<InvestigationReasoningHistoryResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/history`);
}

export function respondToInvestigationInquiry(
  investigationId: string,
  inquiryId: string,
  responseType: InquiryResponseActionDto,
  text?: string,
) {
  return apiRequest<InquiryResponseResultDto>(
    `/v1/investigations/${encodeURIComponent(investigationId)}/inquiries/${encodeURIComponent(inquiryId)}/responses`,
    {
      method: 'POST',
      body: JSON.stringify({ response_type: responseType, text: text?.trim() || undefined }),
    },
  );
}

export function pinInvestigationEvidence(investigationId: string, artifactType: string, artifactId: string, note?: string) {
  return apiRequest<InvestigationDetailResponseDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/evidence`, {
    method: 'POST',
    body: JSON.stringify({ artifact_type: artifactType, artifact_id: artifactId, note: note?.trim() || undefined }),
  });
}

export type RelationshipDecisionDto = 'SAME_QUESTION' | 'DISTINCT_QUESTIONS' | 'INSUFFICIENT_INFORMATION';

export interface ReconciliationCandidateDto {
  uncertainty_id: string;
  uncertainty_statement: string;
  causal_hypothesis_id: string;
  causal_hypothesis_statement: string;
  shared_evidence_refs: string[];
  condition_hypothesis_ids: string[];
  warning: string;
}
export interface ReconciliationReviewDto {
  uncertainty_id: string;
  causal_hypothesis_id: string;
  reasoning_version: number;
  decision: RelationshipDecisionDto;
  rationale: string;
  reviewed_by: string;
  reviewed_at: string;
  evidence_refs: string[];
  effect: 'RELATIONSHIP_ONLY';
}
export interface ReconciliationListDto {
  reasoning_version: number | null;
  reviewable: boolean;
  candidates: ReconciliationCandidateDto[];
  decisions: ReconciliationReviewDto[];
}
export function listInvestigationReconciliations(investigationId: string) {
  return apiRequest<ReconciliationListDto>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/reconciliations`);
}
export function recordInvestigationReconciliation(
  investigationId: string,
  request: {
    expected_reasoning_version: number;
    uncertainty_id: string;
    causal_hypothesis_id: string;
    decision: RelationshipDecisionDto;
    rationale: string;
    evidence_refs: string[];
  },
) {
  return apiRequest<{ review: ReconciliationReviewDto }>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/reconciliations`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Separate mode-selected V1 surface. Legacy endpoints remain unchanged.
import type { DiagnosticV1EvidenceReviewDraft, DiagnosticV1FeedbackDraft, DiagnosticV1FloorView } from '../types/active-reasoning-v1';
export interface V1ReasoningResponse {
  writer: 'LEGACY' | 'V1';
  view: DiagnosticV1FloorView | null;
}
export function getInvestigationReasoningV1(investigationId: string) {
  return apiRequest<V1ReasoningResponse>(`/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/v1`);
}
export function submitInvestigationV1Feedback(investigationId: string, draft: DiagnosticV1FeedbackDraft) {
  return apiRequest<{ feedback_id: string; evidence_id: string; created: boolean; scheduling_status: string; effect: string }>(
    `/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/v1/feedback`,
    { method: 'POST', body: JSON.stringify(draft) },
  );
}

/** Durable human evidence review; never authorizes physical action or runs AI. */
export function getInvestigationV1EvidenceReviews(investigationId: string) {
  return apiRequest<{ investigation_id: string; reviews: import('../types/active-reasoning-v1').DiagnosticV1ReviewHistoryItem[] }>(
    `/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/v1/evidence-review`,
  );
}
export function submitInvestigationV1EvidenceReview(investigationId: string, draft: DiagnosticV1EvidenceReviewDraft) {
  return apiRequest<{ review_id: string; created: boolean; decision: string; reassessment_status: string; assessment_status: 'STALE' }>(
    `/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/v1/evidence-review`,
    { method: 'POST', body: JSON.stringify(draft) },
  );
}

/** Authorized scheduling of one previously accepted evidence-review intent. */
export function scheduleInvestigationV1AcceptedReview(investigationId: string, reviewId: string) {
  return apiRequest<{ review_id: string; reassessment_status: string; intent_id: string; created: boolean }>(
    `/v1/investigations/${encodeURIComponent(investigationId)}/reasoning/v1/evidence-review/reassess`,
    { method: 'POST', body: JSON.stringify({ review_id: reviewId }) },
  );
}
